import {
  INVOICE_RUNS_PATH,
  bulkActionFileDownloadPath,
  invoiceRunDownloadPath,
  invoiceRunPath,
  type AppRole,
  type InvoiceRun,
  type InvoiceStatus,
} from "@somasi/shared";
import { apiFetch } from "./http";
import type { FeeSource } from "@/lib/pricing";

/** Domain endpoints that are more than a table read or write. */

export interface MeResponse {
  id: string;
  email: string | null;
  roles: AppRole[];
  /**
   * The caller's name, from their employee record where they have one.
   *
   * It used to be a `profile` object from a table that shadowed the Cognito
   * pool. There is no such table: the person is the record, and an account with
   * no record falls back to the pool's own `name` attribute.
   */
  full_name: string | null;
  /** Their employee id, where they are somebody on the register. */
  employee_id: string | null;
}

export async function fetchMe(): Promise<MeResponse | null> {
  const { data } = await apiFetch<MeResponse>("/me");
  return data;
}

export async function bootstrapAccess(): Promise<{ bootstrapped: boolean; roles?: AppRole[] }> {
  const { data } = await apiFetch<{ bootstrapped: boolean; roles?: AppRole[] }>("/me/bootstrap", {
    method: "POST",
  });
  return data ?? { bootstrapped: false };
}

/** Mirrors `ResolvedService` in `services/api/src/lib/billing.ts`. */
export interface ResolvedService {
  service_id: string;
  code: string;
  name: string;
  description: string;
  fee: number;
  /** Which level the fee came from, after the cascade. */
  source: FeeSource;
  billing_frequency: string | null;
  /** True when the company has subscribed to this service, not merely priced it. */
  in_plan: boolean;
  company_service_id: string | null;
  /**
   * The date of the schedule version this fee was read from, or `null` when it
   * came from the catalogue or a typed-in company fee.
   */
  source_effective_from: string | null;
  /**
   * What the Somasi standard price list charges on `as_of` — the rate the
   * "Somasi standard" option on an invoice line bills. Read from the schedule
   * carrying `is_standard`, at the version in force on that date, falling back
   * to the catalogue's own fee when that list does not price the service.
   */
  standard_fee: number;
  /**
   * The date of the standard price list version `standard_fee` came from, or
   * `null` when it fell back to the catalogue's own fee.
   */
  standard_effective_from: string | null;
  /**
   * The client group schedule's fee, or `null` when no group schedule prices
   * this service. Reported even for a company whose own mode does not read it,
   * so an invoice line may be raised at the group's rate by explicit choice.
   */
  group_fee: number | null;
  /** The date of the group schedule version `group_fee` came from. */
  group_effective_from: string | null;
}

/**
 * The price list a company is on, as at a date.
 *
 * `asOf` is the date the prices are wanted *for* — an invoice's own date, not
 * today. A schedule holds a version per period, so omitting it prices a
 * back-dated invoice at whatever the latest rates happen to be.
 */
export async function fetchPriceList(companyId: string, asOf?: string): Promise<ResolvedService[]> {
  const query = asOf ? `?as_of=${encodeURIComponent(asOf)}` : "";
  const { data } = await apiFetch<ResolvedService[]>(
    `/companies/${encodeURIComponent(companyId)}/price-list${query}`,
  );
  return data ?? [];
}

/**
 * The price list a client group is on, as at a date.
 *
 * The group's schedule falling back to the catalogue, with no company under it —
 * what an invoice raised to the group as a whole is priced from. Same shape as
 * the company list, so a page reads one or the other without branching.
 */
export async function fetchClientGroupPriceList(
  clientGroupId: string,
  asOf?: string,
): Promise<ResolvedService[]> {
  const query = asOf ? `?as_of=${encodeURIComponent(asOf)}` : "";
  const { data } = await apiFetch<ResolvedService[]>(
    `/client-groups/${encodeURIComponent(clientGroupId)}/price-list${query}`,
  );
  return data ?? [];
}

/** Mirrors `ServiceSchedulePrice` in `services/api/src/lib/billing.ts`. */
export interface ServiceSchedulePrice {
  fee_schedule_id: string;
  schedule_name: string;
  active: boolean;
  /** The Somasi standard price list. Absent from an API that predates the flag. */
  is_standard?: boolean;
  fee: number;
  billing_frequency: string | null;
  /** The start of the dated set this rate comes from. */
  effective_from: string;
  /** Its last day, or `null` while it is the newest set. Derived, never stored. */
  effective_to: string | null;
  version_count: number;
}

/** Every schedule that prices one service today, at the rate it bills today. */
export async function fetchServiceSchedules(serviceId: string): Promise<ServiceSchedulePrice[]> {
  const { data } = await apiFetch<ServiceSchedulePrice[]>(
    `/services/${encodeURIComponent(serviceId)}/fee-schedules`,
  );
  return data ?? [];
}

/** What issuing a draft settles: its date, its due date and its real number. */
export interface IssuedInvoice {
  id: string;
  invoice_number: string;
  invoice_date: string;
  due_date: string | null;
  status: InvoiceStatus;
}

/**
 * Dates a draft and numbers it out of the series the client sees.
 *
 * The only way out of draft. A number used to be allocated when the new invoice
 * page *opened*, which spent one from the client-facing series on every page view
 * — the series had passed 0430 behind six invoices. Nothing is allocated now
 * until the invoice is issued here.
 */
export async function issueInvoice(
  invoiceId: string,
  // Both dates required, as the endpoint requires them: an issued invoice is
  // dated and has a day it is due by.
  on: { invoice_date: string; due_date: string; status?: InvoiceStatus },
): Promise<IssuedInvoice> {
  const { data, error } = await apiFetch<IssuedInvoice>(
    `/invoices/${encodeURIComponent(invoiceId)}/issue`,
    // `on` itself, not `JSON.stringify(on)`: `apiFetch` serialises the body. A
    // string here is encoded a second time, so the handler parses a string
    // rather than an object, every field reads `undefined` off it without
    // throwing, and the invoice issues dated today with its due date dropped.
    { method: "POST", body: on },
  );
  if (error) throw new Error(error.message);
  if (!data) throw new Error("The API did not return the issued invoice");
  return data;
}

export interface CreateInvoiceInput {
  /** Blank to let the server number it — a draft series number, or a real one. */
  invoice_number: string;
  /** Only kept when the invoice is created as already issued; a draft is undated. */
  invoice_date: string | null;
  due_date: string | null;
  /** `null` when the invoice is raised to the client group as a whole. */
  company_id: string | null;
  client_group_id: string | null;
  bill_to_contact_id: string | null;
  bank_account_id: string | null;
  note_ids: string[];
  period_label: string | null;
  notes: string | null;
  /** What this invoice prints where it differs from the records behind it. */
  terms: string | null;
  bill_to_name: string | null;
  bill_to_for: string | null;
  bill_to_address: string | null;
  from_name: string | null;
  from_address: string | null;
  status: "draft" | "sent";
  lines: {
    description: string;
    quantity: number;
    unit_amount: number;
    service_id: string | null;
    source_type: string;
    source_id: string | null;
    /**
     * When the work was delivered, as YYYY-MM-DD.
     *
     * On a line the invoice composed — a catalogue service, or a charge named on
     * the line — this is what the writer logs the work under, billed against the
     * new invoice. A line drawn from the work log sends the date it is already
     * recorded under and is not logged twice.
     */
    service_date: string | null;
  }[];
}

/** Creates the header, its lines and the source stamps in one request. */
export async function createInvoice(
  input: CreateInvoiceInput,
): Promise<{ id: string; invoice_number: string }> {
  // The number comes back because the server allocates it: a draft takes one out
  // of the draft series, so the page cannot know it before saving.
  const { data } = await apiFetch<{ id: string; invoice_number: string }>("/invoices", {
    method: "POST",
    body: input,
  });
  if (!data?.id) throw new Error("The API did not return the new invoice");
  return data;
}

export async function requestInvoiceArchive(invoiceId: string): Promise<void> {
  await apiFetch(`/invoices/${encodeURIComponent(invoiceId)}/archive`, { method: "POST" });
}

export async function getInvoiceDownloadUrl(invoiceId: string): Promise<string> {
  const { data } = await apiFetch<{ url: string }>(
    `/invoices/${encodeURIComponent(invoiceId)}/download`,
  );
  if (!data?.url) throw new Error("The invoice PDF could not be prepared");
  return data.url;
}

/** A short link to one of the files a bulk action left; it does not expire with the email. */
export async function getBulkActionFileUrl(
  bulkActionId: string,
  fileIndex: number,
): Promise<string> {
  const { data } = await apiFetch<{ url: string }>(
    bulkActionFileDownloadPath(bulkActionId, fileIndex),
  );
  if (!data?.url) throw new Error("The file could not be prepared");
  return data.url;
}

/** A link to the chosen invoices bound into one PDF, in the order given. */
export async function getInvoicesDownloadUrl(invoiceIds: readonly string[]): Promise<string> {
  const { data } = await apiFetch<{ url: string }>("/invoices/download", {
    method: "POST",
    body: { invoice_ids: invoiceIds },
  });
  if (!data?.url) throw new Error("The combined PDF could not be prepared");
  return data.url;
}

/**
 * The selected invoices' filed copies joined into one PDF, via `POST /invoices/merge`.
 * Up to 500, so a selection reaching across the list's pages fits in one file.
 */
export async function getMergedInvoicesUrl(invoiceIds: readonly string[]): Promise<string> {
  const { data } = await apiFetch<{ url: string }>("/invoices/merge", {
    method: "POST",
    body: { invoice_ids: invoiceIds },
  });
  if (!data?.url) throw new Error("The merged PDF could not be prepared");
  return data.url;
}

/** What the bulk dialog sends to start a run: who to invoice, for what, and how. */
export type InvoiceRunRequest = Pick<
  InvoiceRun,
  | "title"
  | "company_ids"
  | "bundle_ids"
  | "invoice_date"
  | "due_date"
  | "service_date"
  | "period_label"
  | "bank_account_id"
  | "issue"
  | "excluded_note_ids"
>;

/** Starts a bulk run on the server; it carries on whether or not the page stays open. */
export async function startInvoiceRun(request: InvoiceRunRequest): Promise<InvoiceRun> {
  const { data } = await apiFetch<InvoiceRun>(INVOICE_RUNS_PATH, {
    method: "POST",
    body: request,
  });
  if (!data) throw new Error("The run could not be started");
  return data;
}

export async function getInvoiceRun(runId: string): Promise<InvoiceRun> {
  const { data } = await apiFetch<InvoiceRun>(invoiceRunPath(runId));
  if (!data) throw new Error("No such run");
  return data;
}

/** A short link to the run's combined PDF, while the run's own link has not expired. */
export async function getInvoiceRunDownloadUrl(runId: string): Promise<string> {
  const { data } = await apiFetch<{ url: string }>(invoiceRunDownloadPath(runId));
  if (!data?.url) throw new Error("The combined PDF could not be prepared");
  return data.url;
}

export interface InvoiceDraftInput {
  /** Dropped on the way in while the invoice is a draft — a draft has no date. */
  invoice_date: string | null;
  due_date: string | null;
  company_id: string;
  client_group_id: string | null;
  period_label: string | null;
  notes: string | null;
  bank_account_id: string | null;
  note_ids: string[];
  lines: {
    description: string;
    quantity: number;
    unit_amount: number;
    service_id: string | null;
    source_type: string;
    source_id: string | null;
  }[];
}

/**
 * Hands a batch of drafts to the queue. Returns once accepted; a worker writes
 * the invoices, numbers them without collisions and archives each PDF.
 */
export async function queueBulkInvoices(
  drafts: InvoiceDraftInput[],
): Promise<{ queued: number; batchId: string }> {
  const { data } = await apiFetch<{ queued: number; batchId: string }>("/invoices/bulk", {
    method: "POST",
    body: { drafts },
  });
  return data ?? { queued: 0, batchId: "" };
}

export interface ImportCommitResult {
  mode: "applied" | "queued";
  imported: number;
  batchId: string;
}

/**
 * The tables the bulk importer will write — `IMPORTABLE` in
 * `services/api/src/handlers/import-data.ts`, which refuses anything else.
 */
export type ImportTable =
  "client_groups" | "companies" | "contacts" | "service_events" | "sundry_expenses";

export async function commitImport(
  table: ImportTable,
  rows: Record<string, unknown>[],
): Promise<ImportCommitResult> {
  const { data } = await apiFetch<ImportCommitResult>("/import/commit", {
    method: "POST",
    body: { table, rows },
  });
  if (!data) throw new Error("The import did not complete");
  return data;
}
